import type { PipeInterface } from '@render/composables/usePipe'
import type { YunoCheckoutSession } from '@render/composables/yunoSession'
import { usePaymentData } from '@render/composables/usePaymentData'
import usePaymentSession from '@render/composables/usePaymentSession'
import { useFetch } from '@render/composables/useFetch'
import { recordLatestPaymentSession } from '@render/composables/paymentSessionStore'

// Pre-creating the checkout session on plan selection, so YunoCheckout's mount is a cache hit
// instead of a ~400ms POST. `useYuno.getOrFetchYunoSession` already returns the cached entry when
// the fingerprint matches and it has not expired, so nothing new is needed on the mount side —
// this only has to write the SAME shape `fetchFreshSession` writes.
//
// The fingerprint is the whole reason this file exists rather than living in the integration: it is
// `JSON.stringify` of the ENTIRE sorted payload, so a pre-created session is only reused if the
// payload is byte-identical to what YunoCheckout will build. Two copies of that builder would
// silently drift into permanent cache misses — the failure mode being "no benefit", which nothing
// would alert on. So both sides go through `buildYunoPaymentData` and `paymentDataKey` here.

// Pipe-state cache key for the session — sibling to Primer's `var.global.primerSession`. It lives
// here because both the pre-create and useYuno read/write it, and two copies of the literal would be
// free to drift.
export const YUNO_SESSION_KEY = 'var.global.yunoSession'

// The fields that actually determine the session payments-api creates: who is paying, for which
// offer, in which currency, against which order. A change to any of these makes a cached session
// wrong and it must be refetched — that is what this fingerprint is for.
//
// Everything else in the payload is attribution/telemetry recorded alongside the session (utm*, fbp,
// fbc, gaCid, gaSessionId, fraudNetSessionId, originUrl, trackingProperties, source, isTestPayment).
// It does not affect the amount or the offer, and crucially some of it is VOLATILE: buildRealPaymentData
// re-reads cookies on every call, and `trackingProperties.query_string` is `window.location.search`
// at call time. Fingerprinting the whole payload therefore made a pre-created session impossible to
// reuse — the plan page and the checkout page can never produce the same string — which is exactly
// the bug this narrowing fixes.
const SESSION_IDENTITY_FIELDS = [
  'brandCode',
  'currencyCode',
  'customerId',
  'orderId',
  'pipelineOfferType',
  'priceProductObjectId',
  'provider',
  'restrictSaveCard',
] as const

// Stable serialization of the session-identifying half of the payload — the cache fingerprint. Must
// stay identical on both sides, which is why it is exported from one place.
export const paymentDataKey = (data: Record<string, unknown>): string => {
  const sorted = [...SESSION_IDENTITY_FIELDS].sort().map((k) => [k, data[k] ?? null] as const)
  return JSON.stringify(sorted)
}

// Names the session-identity fields that differ between two fingerprints. Used to decide whether a
// change is repriceable (below) and, temporarily, to explain a cache miss at the checkout.
export const diffPaymentDataKeys = (a: string, b: string): string[] => {
  const parse = (raw: string): Record<string, unknown> => {
    try {
      return Object.fromEntries(JSON.parse(raw) as [string, unknown][])
    } catch {
      return {}
    }
  }
  const left = parse(a)
  const right = parse(b)
  return [...new Set([...Object.keys(left), ...Object.keys(right)])].filter(
    (key) => JSON.stringify(left[key]) !== JSON.stringify(right[key]),
  )
}

// Mirrors YunoCheckout.vue's `getPriceProductObjectId`. The exit-intent branch is reachable here
// too: a visitor who accepts the exit offer and returns to plan selection must pre-create against
// the exit ppo, not the regular one.
const resolvePriceProductObjectId = (
  pipe: Pick<PipeInterface, 'getData'>,
  pipelineOfferType: string,
): string | undefined => {
  const isExitIntent = pipe.getData('var.global.hasExitIntentBeenAccepted')

  if (isExitIntent && pipelineOfferType === 'BASIC') {
    return pipe.getData('var.global.selectedOfferGroup.exit.ppoId') as string | undefined
  }

  return pipe.getData('var.global.selectedOfferGroup.regular.ppoId') as string | undefined
}

// The exact payload YunoCheckout posts. `restrictSaveCard: true` is not incidental — server-side it
// is the first branch of PaymentsService.createPayment, returning a client session before any of the
// charging paths (free-trial-with-vault, offset charge, MIT on a saved card). That is what makes it
// safe to call this from the funnel: the endpoint cannot charge anyone in this shape.
export const buildYunoPaymentData = (
  pipe: PipeInterface,
  isBrowser: boolean,
  pipelineOfferType: string,
): Record<string, unknown> => {
  const { buildRealPaymentData } = usePaymentData(pipe, isBrowser)

  return {
    ...buildRealPaymentData({
      pipelineOfferType,
      priceProductObjectId: resolvePriceProductObjectId(pipe, pipelineOfferType),
      restrictSaveCard: true,
    }),
    provider: 'yuno',
  }
}

// Fields the session POST cannot be made without. payments-api derives the order and the offer from
// them, so posting with any missing would just 4xx — and on the funnel there is no user waiting on
// the result, so there is nothing to report and no reason to try.
const REQUIRED_FIELDS = ['customerId', 'orderId', 'priceProductObjectId'] as const

const hasPrereqs = (payload: Record<string, unknown>): boolean =>
  REQUIRED_FIELDS.every((field) => {
    const value = payload[field]
    return value !== undefined && value !== null && value !== ''
  })

// Fields a reprice is allowed to change. Anything else that moves means this is a DIFFERENT session —
// a different customer, order, brand, currency or provider — and repricing across that would point the
// visitor at a session that is not theirs. `pipelineOfferType` rides along because it is derived from
// the same offer choice.
const REPRICEABLE_FIELDS = ['priceProductObjectId', 'pipelineOfferType']

const isPlanChangeOnly = (
  cachedFingerprint: string | undefined,
  nextFingerprint: string,
): boolean => {
  if (!cachedFingerprint) return false
  const changed = diffPaymentDataKeys(cachedFingerprint, nextFingerprint)
  return changed.length > 0 && changed.every((field) => REPRICEABLE_FIELDS.includes(field))
}

// True once any ppo has been charged in this funnel run. Reads the same list `isPpoPurchased` consults,
// which YunoCheckout/PrimerCheckout/SavedCard all populate via `recordPurchase` on completion.
const hasPurchasedAnything = (pipe: PipeInterface): boolean => {
  const purchased = pipe.getData('var.global.purchasedPpos')
  return Array.isArray(purchased) && purchased.length > 0
}

type CachedEntry = {
  checkoutSession?: string
  paymentDataKey?: string
  paymentSessionId?: string
  publicApiKey?: string
  countryCode?: string
  language?: string
  sessionExpirationDate?: string
}

// `const.global.paymentsApiUrl` points at the session-CREATE endpoint (POST /api/v1/payments). The
// reprice lives on a sibling route, so it is derived from the same constant rather than adding a
// second one for authors to keep in sync — a mismatch between the two would be silent.
const paymentSessionsUrl = (paymentsUrl: string, paymentSessionId: string): string | null => {
  const marker = '/api/v1/payments'
  if (!paymentsUrl.includes(marker)) return null
  return `${paymentsUrl.slice(0, paymentsUrl.indexOf(marker))}/api/v1/payment-sessions/${paymentSessionId}`
}

// Reprice the session we already hold instead of creating another one.
//
// PATCH /api/v1/payment-sessions/:id re-derives amount/currency/descriptor from the new PPO, saves the
// PaymentSession row, and PATCHes Yuno's checkout session with the new amount and refreshed metadata —
// returning the SAME checkout_session. So the cached entry keeps its checkoutSession and only its
// fingerprint moves, and the visitor ends the funnel with one session no matter how many plans they
// tried. Returns false when the reprice is not possible, so the caller can fall back to creating.
const repriceSession = async (
  pipe: PipeInterface,
  paymentsUrl: string,
  cached: CachedEntry,
  payload: Record<string, unknown>,
  fingerprint: string,
): Promise<boolean> => {
  if (!cached.paymentSessionId || !cached.checkoutSession) return false

  const url = paymentSessionsUrl(paymentsUrl, cached.paymentSessionId)
  if (!url) return false

  const { error, patch } = useFetch()
  await patch(url, {
    body: {
      brandCode: payload.brandCode,
      provider: payload.provider,
      // The reprice target. payments-api resolves it to a PriceProductObject and recomputes the money.
      priceProductObjectId: payload.priceProductObjectId,
      // Yuno's checkout_session — payments-api PATCHes it upstream and echoes it back unchanged.
      clientToken: cached.checkoutSession,
    },
    timeoutMs: 30_000,
  })
  if (error.value) return false

  // Only the fingerprint changes: the session id, checkout session and key all still describe the
  // same (now repriced) session, and useYuno's cache check compares nothing else.
  pipe.setData(YUNO_SESSION_KEY, { ...cached, paymentDataKey: fingerprint })
  console.log('[yuno-precreate] session repriced in place') // TEMPORARY
  return true
}

// Fingerprints with a POST currently in flight. The cached-session check below cannot cover this on
// its own: two triggers firing close together (a nested offer write landing in stages, a debounce
// boundary) both read the cache BEFORE either has written it, so both would post — which is how the
// same visitor ends up with two checkout sessions and inconsistent behaviour at the mount.
const inFlight = new Set<string>()

// Fire-and-forget. Never throws and never surfaces anything: the checkout still fetches its own
// session on mount, so the only cost of a failure here is that the visitor gets today's behaviour.
export const preCreateYunoSession = async (
  pipe: PipeInterface,
  pipelineOfferType: string,
): Promise<void> => {
  const paymentsUrl = pipe.getData('const.global.paymentsApiUrl') as string | undefined
  if (!paymentsUrl) return

  // Stop once anything has been purchased in this funnel run.
  //
  // `var.global.selectedOfferGroup` is not exclusive to plan selection — an UPSELL button later in the
  // funnel can be authored to write the same variable, and upsells always come AFTER the basic
  // checkout. Without this gate that write would land here with the hardcoded BASIC pipelineOfferType
  // and the upsell's ppo, and because only `priceProductObjectId` moved, `isPlanChangeOnly` would read
  // it as a plan change and PATCH the basic session — which by then has already been PAID — to the
  // upsell's price.
  //
  // The purchased list is the right gate rather than a page or component check: it is written by
  // `recordPurchase` from the checkout's own completion path, so it means "a charge happened", which
  // is exactly when pre-creating stops being about the checkout ahead of the visitor. The upsell's own
  // YunoCheckout still creates its session at mount, as it does today.
  if (hasPurchasedAnything(pipe)) {
    console.log('[yuno-precreate] SKIPPED — a purchase already completed (upsell territory)') // TEMPORARY
    return
  }

  const payload = buildYunoPaymentData(pipe, true, pipelineOfferType)
  if (!hasPrereqs(payload)) {
    // TEMPORARY
    console.log(
      '[yuno-precreate] SKIPPED — missing prereqs:',
      REQUIRED_FIELDS.filter((f) => !payload[f]),
    )
    return
  }

  const fingerprint = paymentDataKey(payload)

  // Already hold a session for this exact payload — re-posting would mint a second checkout session
  // on Yuno's side and a second PaymentSession row on ours for no gain. Expiry is deliberately NOT
  // checked here: an expiring entry is useYuno's problem to refetch at mount, and re-creating on a
  // funnel step the visitor may never leave would just churn sessions.
  const cached = pipe.getData(YUNO_SESSION_KEY) as CachedEntry | null | undefined
  if (cached?.checkoutSession && cached.paymentDataKey === fingerprint) {
    console.log('[yuno-precreate] already cached for this payload — skipping') // TEMPORARY
    return
  }

  // A session exists but for a different plan — reprice it rather than minting a second one. Only
  // attempted when the ONLY thing that changed is the offer: a different customer or order is a
  // different session entirely, and repricing across those would point the visitor at someone
  // else's session.
  if (
    cached?.checkoutSession &&
    cached.paymentSessionId &&
    isPlanChangeOnly(cached.paymentDataKey, fingerprint)
  ) {
    if (inFlight.has(fingerprint)) {
      console.log('[yuno-precreate] already in flight for this payload — skipping') // TEMPORARY
      return
    }
    inFlight.add(fingerprint)
    try {
      if (await repriceSession(pipe, paymentsUrl, cached, payload, fingerprint)) return
      // Reprice failed — fall through and create a fresh session so the visitor is never left with a
      // session whose amount no longer matches the plan they picked.
    } finally {
      inFlight.delete(fingerprint)
    }
  }

  if (inFlight.has(fingerprint)) {
    console.log('[yuno-precreate] already in flight for this payload — skipping') // TEMPORARY
    return
  }
  inFlight.add(fingerprint)

  try {
    const { isError, data, fetchPaymentSession } = usePaymentSession<YunoCheckoutSession>(
      paymentsUrl,
      payload,
    )
    await fetchPaymentSession()
    if (isError.value) return

    const session = data.value
    // `instantlyCharged` should be unreachable with restrictSaveCard, but if it ever is, there is no
    // session to cache and the charge has already happened — leave it entirely to the checkout,
    // which knows how to complete on it.
    if (!session?.checkoutSession || session.instantlyCharged) return

    const publicApiKey =
      session.publicApiKey || (pipe.getData('const.global.yunoPublicApiKey') as string) || ''
    if (!publicApiKey) return

    // Same shape `fetchFreshSession` writes, field for field — anything less and useYuno's cache
    // check or renderYunoCheckout would read undefined off it.
    const entry = {
      checkoutSession: session.checkoutSession,
      publicApiKey,
      countryCode: session.countryCode || (pipe.getData('const.system.country') as string) || 'US',
      language: session.language || (pipe.getData('const.system.language') as string) || 'en',
      sessionExpirationDate: session.sessionExpirationDate || '',
      paymentDataKey: fingerprint,
      paymentSessionId: session.paymentSessionId || '',
    }
    pipe.setData(YUNO_SESSION_KEY, entry)
    recordLatestPaymentSession(pipe, { ...session, ...entry })
    console.log('[yuno-precreate] session cached', { fingerprint }) // TEMPORARY
  } catch {
    // Swallowed by design — see the note above the function.
  } finally {
    inFlight.delete(fingerprint)
  }
}
